<template>
  <div :class="rootClasses" :style="rootStyles">
    <ElementControls
      v-if="editing"
      :no-page-break="!pageBreakAllowed"
      @delete="$emit('delete')"
      @duplicate="$emit('duplicate')"
      @page-break="$emit('page-break')"
      @hide="$emit('hide')"
    />

    <div :class="layoutClasses">
      <div
        v-if="label || description"
        class="formester-label-description-wrapper"
        :style="labelDescriptionWrapperStyles"
      >
        <label
          :class="labelClasses"
          :style="labelStyles"
          :for="item.label + item.id"
          v-html="label"
        />
        <SmartText
          :active="active"
          :editing="editing"
          :item="item"
          :style="descriptionStyles"
          :text="description"
        />
      </div>

      <div class="stars-wrapper">
        <div
          v-for="num in range(1, item.count)"
          :key="num"
          :class="starClasses(num)"
          :style="starColor"
          class="rating-star"
          tabindex="0"
          @blur="handleBlur"
          @focus="handleFocus(num)"
          @keypress.space="handleSelect(num)"
          @click="handleSelect(num)"
          @mouseover="handleHover(num)"
          @mouseout="handleHover(null)"
        >
          <StarIcon class="star" :stroke="starColor.color" />
          <span
            v-if="item.showRatingNumbers"
            class="star-rating-number"
            :style="subLabelStyles"
          >
            {{ num }}
          </span>
        </div>
      </div>
      <ErrorMessage :item="item" />
      <input
        :id="item.label"
        class="star-rating-input"
        :value="item.value"
        :name="`${item.label}`"
        :required="item.required"
        :disabled="isDisabled"
      />
    </div>
  </div>
</template>

<script>
import ElementControls from "@/components/form_fields/shared/element-controls.vue";
import commonStyles from "@/components/form_fields/shared/commonStyles";
import sharedActions from "@/components/form_fields/shared/sharedActions";
import ErrorMessage from "@/components/form_fields/shared/error-message.vue";
import SmartText from "@/components/form_fields/shared/smart-text.vue";
import { set } from "lodash-es";
import StarIcon from "@/components/builder/components/star-icon.vue";

export default {
  name: "StarRating",
  components: {
    ElementControls,
    ErrorMessage,
    SmartText,
    StarIcon,
  },
  mixins: [commonStyles, sharedActions],
  props: {
    item: {
      type: Object,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    styling: {
      type: Object,
      required: true,
    },
    pageBreakAllowed: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["delete", "page-break", "duplicate", "hide"],
  data() {
    return {
      hovered: null,
      selected: null,
      focused: null,
    };
  },
  computed: {
    inputClasses() {
      return {
        "formester-input": true,
        "formester-input__shorttext": true,
        "form-control": true,
      };
    },
    starColor() {
      return {
        color: this.itemStyling.color || "#fcca00",
        cursor: this.isDisabled && !this.editing ? "not-allowed" : "auto",
        opacity: this.isDisabled && !this.editing ? 0.6 : 1,
      };
    },
  },
  unmounted() {
    this.resetSelected();
  },
  methods: {
    range(start, end) {
      start = parseInt(start);
      end = parseInt(end);
      let res = [];
      for (let i = start; i <= end; i++) {
        res.push(i);
      }
      return res;
    },
    starClasses(num) {
      return {
        active: num <= this.item.value,
        hoverd: num <= this.hovered,
        focused: num == this.focused,
        selected: this.styling.multiPage && num === this.selected,
      };
    },
    handleSelect(num) {
      if (this.editing) return;
      if (this.isDisabled) return;

      this.selected = num;
      set(this.item, "value", num);
      this.autoProgressToNext();
    },
    handleHover(num) {
      this.hovered = num;
    },
    handleFocus(num) {
      if (this.editing) return;
      if (this.isDisabled) return;

      this.focused = num;
    },
    handleBlur() {
      this.focused = null;
    },
    resetSelected() {
      this.selected = null;
    },
  },
};
</script>

<style>
.stars-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.rating-star {
  color: #a0a0a0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.star {
  stroke: v-bind("starColor.color");
  fill: transparent;
}

.rating {
  font-size: 16px;
  color: var(--font-color);
  pointer-events: none;
}

.rating-star:first-child {
  margin-left: 0;
}

.rating-star.hoverd,
.rating-star:focus-visible {
  opacity: 0.6;
}

.rating-star.hoverd > .star,
.rating-star.active > .star,
.rating-star.focused > .star {
  stroke: v-bind("starColor.color");
  fill: v-bind("starColor.color");
}

.rating-star.selected > .star {
  animation: blink 200ms ease 40ms 3;
}

.star-rating-input {
  position: absolute;
  top: 0;
  opacity: 0;
}

@keyframes blink {
  from {
    fill: v-bind("starColor.color");
  }
  to {
    fill: transparent;
  }
}

@media only screen and (max-width: 768px) {
  .star {
    transform: scale(0.9);
  }
}
</style>
